import React, { useState } from "react";
import { Loader, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { api } from "../context/AuthContext";
import {
  autofillAddressFromCurrentLocation,
  getApiErrorMessage,
} from "../utils/geolocation";

/**
 * Reusable "Use Current Location" control for address forms.
 * Calls OSM Nominatim via backend and merges autofilled fields into the form.
 */
export default function LocationAutofillButton({
  onAutofill,
  className = "",
  label = "Use Current Location",
  disabled = false,
}) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (loading || disabled) return;

    setLoading(true);
    try {
      const { fields, geocode } = await autofillAddressFromCurrentLocation(api);

      const filled = ["country", "state_province", "district_county", "city", "postal_code"].filter(
        (key) => String(fields[key] || "").trim()
      );

      if (typeof onAutofill === "function") {
        onAutofill(fields, geocode);
      }

      if (filled.length === 0) {
        toast.message(
          "Location found, but address details were incomplete. Please fill the missing fields."
        );
      } else {
        toast.success(
          geocode?.display_name
            ? "Location detected — address fields updated"
            : "Location detected"
        );
      }
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          "Unable to get your location. Please enter your address manually."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || loading}
      className={
        className ||
        "inline-flex items-center justify-center gap-2 rounded-xl border border-[#ffcc32]/40 bg-[#ffcc32]/10 px-4 py-3 text-sm font-bold text-[#ffcc32] transition hover:bg-[#ffcc32]/20 disabled:cursor-not-allowed disabled:opacity-60"
      }
    >
      {loading ? <Loader size={16} className="animate-spin" /> : <LocateFixed size={16} />}
      {loading ? "Detecting location…" : label}
    </button>
  );
}
